import type { ToolCallGenericMessage } from '@terra/shared-dto-chat-types';
import type { ChatObjectNavigationTarget } from '../../hitl-types';
import { readStringValue, toRecord } from '../shared/network-tool-card-utils';
import {
  formatDataQueryLabel,
  formatDataQueryTitle,
} from './data-query-format.utils';
import { parseQuery, parseResult } from './data-query-parse.utils';

export type DataQueryListItem = {
  key: string;
  label: string;
  method?: string;
  badge?: string;
  navTarget?: ChatObjectNavigationTarget;
};

function readRowId(row: Record<string, unknown>): string | undefined {
  return readStringValue(row, ['id', '_id', 'threadId', 'thread_id']);
}

function readDateValue(
  row: Record<string, unknown>,
  keys: string[],
): string | undefined {
  const direct = readStringValue(row, keys);

  if (direct) {
    return direct;
  }

  for (const key of keys) {
    const wrapped = toRecord(row[key])?.['$date'];

    if (typeof wrapped === 'string') {
      return wrapped;
    }
  }

  return undefined;
}

function formatShortDate(iso: string): string | undefined {
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function mapEndpointRow(
  row: Record<string, unknown>,
): DataQueryListItem | null {
  const id = readRowId(row);

  if (!id) {
    return null;
  }

  const request = toRecord(row.request);
  const method = readStringValue(request, ['method']);
  const path = readStringValue(row, ['path']);
  const url = readStringValue(row, ['url']);
  const label = path ?? url ?? 'Endpoint';

  return {
    key: id,
    label,
    method,
    navTarget: {
      type: 'endpoint',
      id,
      label,
      endpointUrl: url,
    },
  };
}

function mapFindingRow(row: Record<string, unknown>): DataQueryListItem | null {
  const id = readRowId(row);

  if (!id) {
    return null;
  }

  const title = formatDataQueryTitle(
    readStringValue(row, ['title']) ?? 'Finding',
  );
  const severity = readStringValue(row, ['severity']);

  return {
    key: id,
    label: title,
    badge: severity ? formatDataQueryLabel(severity) : undefined,
    navTarget: {
      type: 'finding',
      id,
      label: title,
      mode: 'view',
    },
  };
}

function mapSignalRow(row: Record<string, unknown>): DataQueryListItem | null {
  const id = readRowId(row);

  if (!id) {
    return null;
  }

  const description = readStringValue(row, ['description']);
  const category = readStringValue(row, ['category']);
  const formattedDescription = description
    ? formatDataQueryTitle(description)
    : undefined;
  const label =
    formattedDescription && formattedDescription.length > 72
      ? `${formattedDescription.slice(0, 69)}...`
      : (formattedDescription ?? 'Signal');

  return {
    key: id,
    label,
    badge: category ? formatDataQueryLabel(category) : undefined,
    navTarget: {
      type: 'signal',
      id,
      label,
    },
  };
}

function mapExecutionRow(
  row: Record<string, unknown>,
): DataQueryListItem | null {
  const threadId = readStringValue(row, ['threadId', 'thread_id']);

  if (!threadId) {
    return null;
  }

  const status = readStringValue(row, ['status']);
  const formattedStatus = status ? formatDataQueryLabel(status) : undefined;
  const createdAt = readDateValue(row, ['createdAt', 'created_at']);
  const shortDate = createdAt ? formatShortDate(createdAt) : undefined;
  const label = shortDate ?? 'Execution';
  const navLabel = ['Execution', formattedStatus, shortDate]
    .filter(Boolean)
    .join(' - ');

  return {
    key: threadId,
    label,
    badge: formattedStatus,
    navTarget: {
      type: 'execution',
      id: threadId,
      label: navLabel,
    },
  };
}

function mapHarEntryRow(
  row: Record<string, unknown>,
): DataQueryListItem | null {
  const id = readRowId(row);

  if (!id) {
    return null;
  }

  const host = readStringValue(row, ['requestHost', 'request_host']);
  const path = readStringValue(row, ['requestPath', 'request_path', 'path']);
  const method = readStringValue(row, [
    'requestMethod',
    'request_method',
    'method',
  ]);
  const label = [host, path].filter(Boolean).join('') || 'Request';

  return {
    key: id,
    label,
    method,
    navTarget: {
      type: 'request',
      id,
      label,
    },
  };
}

export function mapDataQueryRows({
  collection,
  rows,
}: {
  collection?: string;
  rows: unknown[];
}): DataQueryListItem[] {
  return rows
    .map((row) => {
      const record = toRecord(row);

      if (!record) {
        return null;
      }

      switch (collection) {
        case 'endpoints':
          return mapEndpointRow(record);
        case 'findings':
          return mapFindingRow(record);
        case 'signals':
          return mapSignalRow(record);
        case 'executions':
          return mapExecutionRow(record);
        case 'harEntries':
          return mapHarEntryRow(record);
        default:
          return null;
      }
    })
    .filter((item): item is DataQueryListItem => item !== null);
}

const TERRA_QUERY_TOOL_NAME = 'mcp__terra__terra_query';
const MIN_OBJECT_LINK_PHRASE_LENGTH = 8;

export type DataQueryObjectLink = {
  phrase: string;
  target: ChatObjectNavigationTarget;
};

type DataQueryToolResult = Pick<
  ToolCallGenericMessage,
  'toolName' | 'status' | 'input' | 'output'
>;

function objectLinkPhrase({
  collection,
  row,
}: {
  collection: string;
  row: Record<string, unknown>;
}): string | undefined {
  if (collection === 'signals') {
    return readStringValue(row, ['description'])?.trim();
  }

  if (collection === 'findings') {
    return readStringValue(row, ['title'])?.trim();
  }

  if (collection === 'endpoints') {
    return readStringValue(row, ['path'])?.trim();
  }

  return undefined;
}

export function collectDataQueryObjectLinks(
  toolResult: DataQueryToolResult,
): DataQueryObjectLink[] {
  if (
    toolResult.toolName !== TERRA_QUERY_TOOL_NAME ||
    toolResult.status !== 'completed'
  ) {
    return [];
  }

  const parsed = parseResult(toolResult.output);

  if (!parsed || parsed.kind !== 'list') {
    return [];
  }

  const collection = parseQuery(toolResult.input)?.collection;

  if (
    collection !== 'signals' &&
    collection !== 'findings' &&
    collection !== 'endpoints'
  ) {
    return [];
  }

  return parsed.rows.flatMap((row) => {
    const record = toRecord(row);

    if (!record) return [];

    const phrase = objectLinkPhrase({ collection, row: record });
    const [item] = mapDataQueryRows({ collection, rows: [record] });

    if (!phrase || !item?.navTarget) return [];

    return [{ phrase, target: item.navTarget }];
  });
}

export function mergeDataQueryObjectLinks(
  links: DataQueryObjectLink[],
): DataQueryObjectLink[] {
  const byPhrase = new Map<string, DataQueryObjectLink | 'ambiguous'>();

  for (const link of links) {
    const phrase = link.phrase.trim();

    if (phrase.length < MIN_OBJECT_LINK_PHRASE_LENGTH) continue;

    const key = phrase.toLowerCase();
    const current = byPhrase.get(key);

    if (!current) {
      byPhrase.set(key, { phrase, target: link.target });
      continue;
    }

    if (current === 'ambiguous') continue;

    if (
      current.target.type !== link.target.type ||
      current.target.id !== link.target.id
    ) {
      byPhrase.set(key, 'ambiguous');
    }
  }

  return [...byPhrase.values()]
    .filter((link): link is DataQueryObjectLink => link !== 'ambiguous')
    .sort((left, right) => right.phrase.length - left.phrase.length);
}

// ponytail: one cache for the window. Move it to a ref if two chat lists mount.
const linksByToolCallId = new Map<string, DataQueryObjectLink[]>();
let cachedObjectLinkSignature = '';
let cachedObjectLinks: DataQueryObjectLink[] = [];

export function dataQueryObjectLinksFor(
  toolResultsById: Record<string, DataQueryToolResult>,
): DataQueryObjectLink[] {
  const ids: string[] = [];

  for (const [id, result] of Object.entries(toolResultsById)) {
    if (
      result.toolName !== TERRA_QUERY_TOOL_NAME ||
      result.status !== 'completed'
    ) {
      continue;
    }

    ids.push(id);

    if (!linksByToolCallId.has(id)) {
      linksByToolCallId.set(id, collectDataQueryObjectLinks(result));
    }
  }

  ids.sort();
  const signature = ids.join('\n');

  if (signature === cachedObjectLinkSignature) {
    return cachedObjectLinks;
  }

  cachedObjectLinkSignature = signature;
  cachedObjectLinks = mergeDataQueryObjectLinks(
    ids.flatMap((id) => linksByToolCallId.get(id) ?? []),
  );

  return cachedObjectLinks;
}
